import { useState, useEffect } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, IconButton, Typography, CircularProgress,
    Snackbar, Menu, MenuItem,
    FormControl, InputLabel, Select, TextField, Checkbox,
    TablePagination, TableSortLabel
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_MEMBERS } from "../graphql/queries";
import EditUserDialog from "./Edituserdialog";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import NotLoggedIn from "../components/LoginExceptions";

const DataTable = () => {
    const { loading, error, data, refetch } = useQuery(GET_MEMBERS);
    const [filters, setFilters] = useState({
        NAME: "",
        MOBILE1: "",
        CITY: "",
        role: "",
    });

    const [fullNameInput, setFullNameInput] = useState("");
    const [openEditModal, setOpenEditModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    // const [selectedUserForDelete, setSelectedUserForDelete] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuUser, setMenuUser] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    // const [dialogMode, setDialogMode] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("");

    // const navigate = useNavigate();
    const storedUserType = localStorage.getItem("user_type");

    useEffect(() => {
        setPage(0);
    }, [filters, fullNameInput]);

    if (!storedUserType) {
        return <NotLoggedIn />;
    }

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleRefresh = () => {
        setFilters({ NAME: "", MOBILE1: "", CITY: "", role: "" });
        setFullNameInput("");
        refetch();
    };

    const handleEdit = (user) => {
        handleMenuClose();
        setSelectedUser(user);
        setOpenEditModal(true);
    };

    const handleMenuOpen = (event, user) => {
        setMenuAnchorEl(event.currentTarget);
        setMenuUser(user);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuUser(null);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const isSelected = (id) => selectedUsers.includes(id);

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const allIds = filteredData.map((user) => user.id);
            setSelectedUsers(allIds);
        } else {
            setSelectedUsers([]);
        }
    };

    const handleCheckboxClick = (event, id) => {
        event.stopPropagation();
        setSelectedUsers((prev) =>
            prev.includes(id) ? prev.filter((uid) => uid !== id) : [...prev, id]
        );
    };

    const filteredData = (data?.zoneMembers || []).filter((row) => {
        const fullName = (row.NAME || "").toLowerCase().trim();
        const searchInput = fullNameInput.toLowerCase().trim();

        const matchesFullName = fullName.includes(searchInput);
        const matchesFilters = Object.keys(filters).every((key) => {
            if (!filters[key]) return true;
            const filterValue = filters[key].toLowerCase();
            const rowValue = row[key]?.toString().toLowerCase();
            if (!rowValue) return false;
            if (key === "role") return rowValue === filterValue;
            return rowValue.includes(filterValue);
        });

        return matchesFullName && matchesFilters;
    });

    // Sorting logic
    const sortedData = [...filteredData].sort((a, b) => {
        if (!orderBy) return 0;
        const valA = (a[orderBy] || "").toString().toLowerCase();
        const valB = (b[orderBy] || "").toString().toLowerCase();
        if (valA < valB) return order === "asc" ? -1 : 1;
        if (valA > valB) return order === "asc" ? 1 : -1;
        return 0;
    });

    return (
        <div style={{ padding: 20 }}>
            {/* Title and Refresh */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <Typography variant="h4" sx={{ fontWeight: "bold", color: "#2A289C" }}>
                    Manage Users
                </Typography>
                <IconButton color="primary" onClick={handleRefresh}>
                    <RefreshIcon />
                </IconButton>
            </div>

            {/* Filters */}
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 10 }}>
                <TextField
                    label="Name"
                    size="small"
                    value={fullNameInput}
                    onChange={(e) => setFullNameInput(e.target.value)}
                    sx={{ flex: "1 1 230px", maxWidth: 230 }}
                />
                <TextField
                    label="Mobile Number"
                    size="small"
                    value={filters.MOBILE1}
                    onChange={(e) => setFilters({ ...filters, MOBILE1: e.target.value })}
                    sx={{ flex: "1 1 230px", maxWidth: 230 }}
                />
                <FormControl size="small" sx={{ flex: "1 1 230px", maxWidth: 230 }}>
                    <InputLabel>User Type</InputLabel>
                    <Select
                        value={filters.role}
                        onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                        label="User Type"
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="SuperUser">SuperUser</MenuItem>
                        <MenuItem value="User">User</MenuItem>
                    </Select>
                </FormControl>
            </div>

            {/* Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead sx={{ backgroundColor: "#FAD707" }}>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    indeterminate={selectedUsers.length > 0 && selectedUsers.length < filteredData.length}
                                    checked={filteredData.length > 0 && selectedUsers.length === filteredData.length}
                                    onChange={handleSelectAllClick}
                                />
                            </TableCell>
                            <TableCell sortDirection={orderBy === "NAME" ? order : false}>
                                <TableSortLabel
                                    active={orderBy === "NAME"}
                                    direction={orderBy === "NAME" ? order : "asc"}
                                    onClick={() => handleRequestSort("NAME")}
                                >
                                    <b>Name</b>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={orderBy === "MOBILE1" ? order : false}>
                                <TableSortLabel
                                    active={orderBy === "MOBILE1"}
                                    direction={orderBy === "MOBILE1" ? order : "asc"}
                                    onClick={() => handleRequestSort("MOBILE1")}
                                >
                                    <b>Mobile Number</b>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={orderBy === "AREA" ? order : false}>
                                <TableSortLabel
                                    active={orderBy === "AREA"}
                                    direction={orderBy === "AREA" ? order : "asc"}
                                    onClick={() => handleRequestSort("AREA")}
                                >
                                    <b>Area</b>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell><b>City</b></TableCell>
                            <TableCell sortDirection={orderBy === "PINCODE" ? order : false}>
                                <TableSortLabel
                                    active={orderBy === "PINCODE"}
                                    direction={orderBy === "PINCODE" ? order : "asc"}
                                    onClick={() => handleRequestSort("PINCODE")}
                                >
                                    <b>Pincode</b>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell><b>User Type</b></TableCell>
                            <TableCell><b>Actions</b></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={8} align="center"><CircularProgress /></TableCell></TableRow>
                        ) : error ? (
                            <TableRow><TableCell colSpan={8} align="center"><Typography color="error">Error loading users</Typography></TableCell></TableRow>
                        ) : sortedData.length > 0 ? (
                            sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                                <TableRow key={row.id} hover selected={isSelected(row.id)}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={isSelected(row.id)}
                                            onChange={(e) => handleCheckboxClick(e, row.id)}
                                        />
                                    </TableCell>
                                    <TableCell>{row.NAME || "N/A"}</TableCell>
                                    <TableCell>{row.MOBILE1 || "N/A"}</TableCell>
                                    <TableCell>{row.AREA || "N/A"}</TableCell>
                                    <TableCell>{row.CITY || "N/A"}</TableCell>
                                    <TableCell>{row.PINCODE || "N/A"}</TableCell>
                                    <TableCell>{row.role || "N/A"}</TableCell>
                                    <TableCell>
                                        <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                                            <MoreVertIcon />
                                        </IconButton>
                                        <Menu
                                            anchorEl={menuAnchorEl}
                                            open={Boolean(menuAnchorEl)}
                                            onClose={handleMenuClose}
                                            PaperProps={{ sx: { boxShadow: "0px 2px 4px rgba(0,0,0,0.1)", borderRadius: 1.5 } }}
                                        >
                                            <MenuItem onClick={() => handleEdit(menuUser)}>
                                                <EditIcon sx={{ color: "blue", mr: 1 }} /> Edit
                                            </MenuItem>
                                        </Menu>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow><TableCell colSpan={8} align="center">No records found.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={sortedData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            {/* Dialogs */}
            <EditUserDialog isOpen={openEditModal} onClose={() => setOpenEditModal(false)} user={selectedUser} onSuccess={refetch} />
            {/* <DeleteConfirmationDialog open={openDialog && dialogMode === "delete"} onClose={() => setOpenDialog(false)} user={selectedUserForDelete} onSuccess={refetch} /> */}

            {/* Snackbar */}
            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)} />
        </div>
    );
};

export default DataTable;
